import React, { useEffect, useState } from "react";
import {
  Box, Modal, Card, Typography, TextField, Button, IconButton,
  FormControlLabel, Checkbox, Divider, CircularProgress, Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import { apiHost } from '../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\/+/, "");
  return `${protocol}://${baseUrl}`;
};

const emptyOption = () => ({ nama: "", hargatambahan: "0" });
const emptyGroup = () => ({ nama: "", wajib: true, options: [emptyOption()] });

// Kelola grup addon (mis. "Level Pedas", "Es") + pilihannya untuk satu produk,
// dipakai lewat tombol "Addon" di halaman Produk. Simpan = ganti semua addon
// produk ini sekaligus (lebih simpel daripada CRUD per baris).
export const ProductOptionsDialog = ({ open, onClose, baranguuid, namabarang }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !baranguuid) return;
    setLoading(true);
    setError("");
    axios
      .get(`${getApiBaseUrl()}/barang/${baranguuid}/opsi`, { withCredentials: true })
      .then((res) => {
        const data = res.data?.data || [];
        setGroups(
          data.length > 0
            ? data.map((g) => ({
                nama: g.nama,
                wajib: g.wajib,
                options: (g.BarangOpsiPilihans || []).map((o) => ({
                  nama: o.nama,
                  hargatambahan: String(o.hargatambahan),
                })),
              }))
            : []
        );
      })
      .catch(() => setError("Gagal memuat addon produk."))
      .finally(() => setLoading(false));
  }, [open, baranguuid]);

  const updateGroup = (gIdx, patch) => {
    setGroups((prev) => prev.map((g, i) => (i === gIdx ? { ...g, ...patch } : g)));
  };

  const updateOption = (gIdx, oIdx, patch) => {
    setGroups((prev) =>
      prev.map((g, i) =>
        i !== gIdx
          ? g
          : { ...g, options: g.options.map((o, j) => (j === oIdx ? { ...o, ...patch } : o)) }
      )
    );
  };

  const addGroup = () => setGroups((prev) => [...prev, emptyGroup()]);
  const removeGroup = (gIdx) => setGroups((prev) => prev.filter((_, i) => i !== gIdx));
  const addOption = (gIdx) =>
    setGroups((prev) =>
      prev.map((g, i) => (i === gIdx ? { ...g, options: [...g.options, emptyOption()] } : g))
    );
  const removeOption = (gIdx, oIdx) =>
    setGroups((prev) =>
      prev.map((g, i) =>
        i === gIdx ? { ...g, options: g.options.filter((_, j) => j !== oIdx) } : g
      )
    );

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.put(
        `${getApiBaseUrl()}/barang/${baranguuid}/opsi`,
        { groups },
        { withCredentials: true }
      );
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Gagal menyimpan addon produk.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}
    >
      <Card sx={{ padding: 4, maxWidth: 560, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <Typography variant="h6" gutterBottom>
          Kelola Addon — {namabarang}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Grup addon yang "Wajib dipilih" akan tampil sebagai pilihan yang harus dipilih (1 dari
          beberapa) saat produk ini ditambahkan ke pesanan, mis. "Level Pedas" atau "Es".
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {groups.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Belum ada addon untuk produk ini.
              </Typography>
            )}

            {groups.map((group, gIdx) => (
              <Box
                key={gIdx}
                sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 2, mb: 2, bgcolor: "#fafafa" }}
              >
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                  <TextField
                    label="Nama Grup"
                    placeholder='mis. "Level Pedas"'
                    size="small"
                    fullWidth
                    value={group.nama}
                    onChange={(e) => updateGroup(gIdx, { nama: e.target.value })}
                  />
                  <IconButton color="error" onClick={() => removeGroup(gIdx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={group.wajib}
                      onChange={(e) => updateGroup(gIdx, { wajib: e.target.checked })}
                    />
                  }
                  label="Wajib dipilih (pilih 1)"
                  sx={{ mb: 1 }}
                />

                {group.options.map((opt, oIdx) => (
                  <Box key={oIdx} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, ml: 2 }}>
                    <TextField
                      label="Nama Pilihan"
                      placeholder='mis. "Level 1"'
                      size="small"
                      fullWidth
                      value={opt.nama}
                      onChange={(e) => updateOption(gIdx, oIdx, { nama: e.target.value })}
                    />
                    <TextField
                      label="Tambahan Harga"
                      size="small"
                      type="number"
                      sx={{ width: 160 }}
                      value={opt.hargatambahan}
                      onChange={(e) => updateOption(gIdx, oIdx, { hargatambahan: e.target.value })}
                    />
                    <IconButton size="small" color="error" onClick={() => removeOption(gIdx, oIdx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={() => addOption(gIdx)} sx={{ ml: 2 }}>
                  Tambah Pilihan
                </Button>
              </Box>
            ))}

            <Button startIcon={<AddIcon />} onClick={addGroup} variant="outlined" sx={{ mb: 2 }}>
              Tambah Grup Addon
            </Button>
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={onClose} disabled={saving}>
            Batal
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
            {saving ? "Menyimpan..." : "Simpan"}
          </Button>
        </Box>
      </Card>
    </Modal>
  );
};

export default ProductOptionsDialog;
